import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { supabase } from '../src/lib/supabaseClient';
import type { OmniSearchResults, OmniSearchResultItem, SearchResultModule } from '../types.ts';
import Spinner from './Spinner.tsx';
import {
    MagnifyingGlassIcon, ShoppingCartIcon, CubeIcon, UsersIcon, MegaphoneIcon,
    DocumentDuplicateIcon, QuestionMarkCircleIcon, ArrowRightIcon, ExclamationTriangleIcon, ClockIcon,
    CreditCardIcon
} from '@heroicons/react/24/outline';

interface OmniSearchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onNavigate: (item: OmniSearchResultItem) => void;
}

const moduleIcons: Record<SearchResultModule, React.ElementType> = {
    Orders: ShoppingCartIcon,
    Products: CubeIcon,
    Customers: UsersIcon,
    Marketing: MegaphoneIcon,
    Invoices: DocumentDuplicateIcon,
    Transactions: CreditCardIcon,
    Help: QuestionMarkCircleIcon,
    Action: ArrowRightIcon,
};

const OmniSearchModal: React.FC<OmniSearchModalProps> = ({ isOpen, onClose, onNavigate }) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<OmniSearchResults | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);

    const flatResults = useMemo(() => {
        if (!results) return [];
        return Object.values(results).filter(Boolean).flat() as OmniSearchResultItem[];
    }, [results]);

    const performSearch = useCallback(async (searchQuery: string) => {
        if (searchQuery.length < 2) {
            setResults(null);
            return;
        }
        setLoading(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user?.id) return;

            const { data, error } = await supabase.rpc('search_by_short_id', {
                p_client_id: session.user.id,
                p_query: searchQuery
            });

            if (error) {
                console.error("OmniSearch RPC failed:", error);
                // Fallback or error handling
            } else if (data) {
                // Map RPC response to OmniSearchResults
                // RPC returns: { customers: [...], orders: [...], products: [...], invoices: [...], transactions: [...] }
                const mappedResults: OmniSearchResults = {};

                if (data.customers?.length) {
                    mappedResults.Customers = data.customers.map((c: any) => ({
                        id: c.nano_id || c.id,
                        module: 'Customers',
                        title: c.name || c.email,
                        description: c.email !== c.title ? c.email : undefined,
                        data: c
                    }));
                }
                if (data.orders?.length) {
                    mappedResults.Orders = data.orders.map((o: any) => ({
                        id: o.nano_id || o.id,
                        module: 'Orders',
                        title: `Order #${o.nano_id ? o.nano_id.substring(0, 8) : o.order_number}`,
                        description: `${o.status} - $${o.total}`,
                        data: o
                    }));
                }
                if (data.products?.length) {
                    mappedResults.Products = data.products.map((p: any) => ({
                        id: p.nano_id || p.id,
                        module: 'Products',
                        title: p.name,
                        description: `SKU: ${p.sku}`,
                        data: p
                    }));
                }
                if (data.invoices?.length) {
                    mappedResults.Invoices = data.invoices.map((i: any) => ({
                        id: i.nano_id || i.id,
                        module: 'Invoices',
                        title: `Inv #${i.nano_id ? i.nano_id.substring(0, 8) : i.invoice_number}`,
                        description: `${i.status} - $${i.amount}`,
                        data: i
                    }));
                }
                if (data.transactions?.length) {
                    mappedResults.Transactions = data.transactions.map((t: any) => ({
                        id: t.nano_id || t.id,
                        module: 'Transactions',
                        title: `Tx #${t.nano_id ? t.nano_id.substring(0, 8) : t.id.substring(0, 8)}`,
                        description: `${t.status} - $${t.amount}`,
                        data: t
                    }));
                }

                setResults(mappedResults);
                setActiveIndex(0);
            }
        } catch (error) {
            console.error("OmniSearch failed:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        const handler = setTimeout(() => {
            performSearch(query);
        }, 300); // 300ms debounce
        return () => clearTimeout(handler);
    }, [query, performSearch]);

    // Focus input on open
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        } else {
            setQuery('');
            setResults(null);
        }
    }, [isOpen]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!isOpen || flatResults.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(prev => (prev + 1) % flatResults.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prev => (prev - 1 + flatResults.length) % flatResults.length);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const selectedItem = flatResults[activeIndex];
                if (selectedItem) {
                    onNavigate(selectedItem);
                }
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, flatResults, activeIndex, onNavigate, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-center items-start pt-12 sm:pt-24" onClick={onClose}>
            <div
                className="fixed inset-0 bg-black/30 backdrop-blur-sm"
                aria-hidden="true"
            ></div>
            <div
                className="relative w-full max-w-2xl bg-white/70 backdrop-blur-xl border border-border rounded-xl shadow-2xl overflow-hidden"
                onClick={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <div className="flex items-center p-4 border-b border-border">
                    <MagnifyingGlassIcon className="h-5 w-5 text-text-subtle mr-3" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search by NanoID, Name, Order #..."
                        className="w-full bg-transparent text-lg text-text placeholder-text-subtle focus:outline-none"
                    />
                </div>
                <div className="max-h-[60vh] overflow-y-auto">
                    {loading && <div className="p-8 flex justify-center"><Spinner /></div>}
                    {!loading && results && Object.keys(results).length === 0 && query.length > 1 && (
                        <div className="p-8 text-center text-text-subtle">No results found for "{query}"</div>
                    )}
                    {!loading && results && Object.keys(results).length > 0 && (
                        <div className="p-2">
                            {(Object.entries(results) as [SearchResultModule, OmniSearchResultItem[]][]).map(([module, items]) => (
                                <div key={module}>
                                    <h3 className="px-3 py-2 text-xs font-semibold text-text-subtle uppercase tracking-wider">{module}</h3>
                                    <ul>
                                        {Array.isArray(items) && items.map(item => {
                                            const Icon = moduleIcons[item.module];
                                            const currentIndex = flatResults.findIndex(r => r.id === item.id && r.module === item.module);
                                            const isActive = currentIndex === activeIndex;
                                            return (
                                                <li
                                                    key={`${item.module}-${item.id}`}
                                                    onClick={() => onNavigate(item)}
                                                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer ${isActive ? 'bg-primary/10 text-primary' : 'text-text hover:bg-gray-100'}`}
                                                >
                                                    <div className="flex items-center">
                                                        {Icon && <Icon className="h-5 w-5 mr-3" />}
                                                        <div>
                                                            <p className="font-medium flex items-center">
                                                                {item.title}
                                                                {item.module === 'Products' && item.lowStock && (
                                                                    <ExclamationTriangleIcon className="h-4 w-4 text-status-warning ml-2" title="Low Stock" />
                                                                )}
                                                                {item.module === 'Orders' && item.isDelayed && (
                                                                    <ClockIcon className="h-4 w-4 text-status-warning ml-2" title="Delayed" />
                                                                )}
                                                            </p>
                                                            {item.description && <p className="text-sm text-text-subtle">{item.description}</p>}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OmniSearchModal;
